/**
 * Tile helpers for drawing (lifted from the dungeon-floors prototype's
 * `dungeon-model.js` and `dungeon-tile-kit.js`). Game `(x, y)` maps to world
 * `(x, 0, -y)`: east is +X and north (+y) is -Z, so north is up on screen
 * (gotcha "contract-and-capture"). Nothing here decides a rule: whether a
 * step is legal is game-core's call; these only say what to draw where.
 */
import type { Floor, Room } from '@ab/game-core';

/** The four orthogonal steps (dx, dy): north, east, south, west. */
export const orthogonalSteps: readonly (readonly [number, number])[] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];

export const tileIndex = (floor: Floor, x: number, y: number): number => y * floor.width + x;

/** A tile that is not wall (floor or exit); outside the grid counts as wall. */
export const isOpen = (floor: Floor, x: number, y: number): boolean =>
  x >= 0 && y >= 0 && x < floor.width && y < floor.height && floor.tiles[tileIndex(floor, x, y)] !== 'wall';

const isWall = (floor: Floor, x: number, y: number): boolean => !isOpen(floor, x, y);

/** Wall neighbours as bits N=1, E=2, S=4, W=8 (the 16-case NESW autotile). Outside the grid is solid. */
export const wallMask = (floor: Floor, x: number, y: number): number =>
  (isWall(floor, x, y + 1) ? 1 : 0) | (isWall(floor, x + 1, y) ? 2 : 0) | (isWall(floor, x, y - 1) ? 4 : 0) | (isWall(floor, x - 1, y) ? 8 : 0);

/** Concave notches NE=1, SE=2, SW=4, NW=8: two orthogonal wall neighbours meeting an open diagonal. */
export const innerCornerMask = (floor: Floor, x: number, y: number): number => {
  const m = wallMask(floor, x, y);
  return (
    ((m & 3) === 3 && !isWall(floor, x + 1, y + 1) ? 1 : 0) |
    ((m & 6) === 6 && !isWall(floor, x + 1, y - 1) ? 2 : 0) |
    ((m & 12) === 12 && !isWall(floor, x - 1, y - 1) ? 4 : 0) |
    ((m & 9) === 9 && !isWall(floor, x - 1, y + 1) ? 8 : 0)
  );
};

export const inRoom = (r: Room, x: number, y: number): boolean => x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;

/** Index of the room holding the tile, or -1 in a corridor. */
export const roomAt = (floor: Floor, x: number, y: number): number => floor.rooms.findIndex((r) => inRoom(r, x, y));

/**
 * A deterministic number in [0, 1) from the floor's seed, a tile and a salt:
 * decoration only (which corridor gets a frame, which wall a rock), never
 * anything the simulation decides.
 */
export const tileRandom = (seed: number, x: number, y: number, salt = 0): number => {
  let n = Math.imul((seed | 0) ^ Math.imul(x + 71, 374761393), 668265263) ^ Math.imul(y + 137, 1274126177) ^ Math.imul(salt + 1, 2246822519);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
};

/** A seeded generator (mulberry32, the prototypes' `engine/src/rng.js`): decoration only. */
export const seededRandom = (seed: number): (() => number) => {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
